import React, { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import Modal from "../componentLibrary/Modal";
import Spinner from "../componentLibrary/Spinner";
import Button from "../componentLibrary/Button";
import {
  invalidatePageFeedbackQueries,
  useGetPageFeedback,
} from "../api/queries";
import { useResolveFeedback } from "../api/mutations";
import { usePermissions } from "../contexts/PermissionContext";
import FeedbackCard from "./FeedbackCard";

interface PageFeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  lessonId: string;
  pageNumber: number;
  pageTitle?: string;
}

const PageFeedbackModal: React.FC<PageFeedbackModalProps> = ({
  isOpen,
  onClose,
  lessonId,
  pageNumber,
  pageTitle,
}) => {
  const [showResolved, setShowResolved] = useState(false);
  const [resolvingId, setResolvingId] = useState<string | null>(null);

  const queryClient = useQueryClient();
  const { hasAllPermissions } = usePermissions();
  const canResolve = hasAllPermissions(["content:update", "analytics:update"]);

  const {
    data: feedback,
    isLoading,
    isError,
  } = useGetPageFeedback(lessonId, pageNumber);

  useEffect(() => {
    if (isOpen && lessonId && pageNumber !== undefined) {
      invalidatePageFeedbackQueries(queryClient, lessonId, pageNumber);
    }
  }, [isOpen, lessonId, pageNumber, queryClient]);

  const resolveMutation = useResolveFeedback({
    onSuccess: async () => {
      invalidatePageFeedbackQueries(queryClient, lessonId, pageNumber);
      setResolvingId(null);
    },
    onError: () => {
      setResolvingId(null);
    },
  });

  const handleResolveToggle = (feedbackId: string, isResolved: boolean) => {
    setResolvingId(feedbackId);
    resolveMutation.mutate({ feedbackId, isResolved });
  };

  const displayedFeedback = feedback
    ? feedback.filter((item) =>
        showResolved ? item.isResolved === true : item.isResolved !== true,
      )
    : [];

  const resolvedCount = feedback
    ? feedback.filter((item) => item.isResolved === true).length
    : 0;

  return (
    <Modal
      title={`Feedback for ${pageTitle || `Page ${pageNumber}`}`}
      subtitle="Viewing all feedback submitted for this page"
      isOpen={isOpen}
      onCloseModal={onClose}
      size="lg"
      height="80vh"
      maxHeight="90vh"
    >
      <div className="space-y-4">
        {isLoading && (
          <div className="flex justify-center py-8">
            <Spinner className="mx-auto" />
          </div>
        )}

        {isError && (
          <div className="text-center text-red-600 p-4">
            Error loading feedback. Please try again.
          </div>
        )}

        {!isLoading && !isError && feedback && feedback.length === 0 && (
          <div className="text-center text-gray-500 p-8">
            <p className="text-lg font-semibold mb-2">No feedback yet</p>
            <p className="text-sm">
              No users have submitted feedback for this page yet.
            </p>
          </div>
        )}

        {!isLoading && !isError && feedback && feedback.length > 0 && (
          <>
            <div className="bg-electrum-50 border border-electrum-200 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-gray-700">
                    Total Feedback Entries
                  </p>
                  <p className="text-2xl font-bold text-electrum-700">
                    {feedback.length}
                  </p>
                </div>
                {canResolve && (
                  <Button
                    variant={showResolved ? "primary" : "secondary"}
                    onClick={() => setShowResolved((prev) => !prev)}
                    className="text-sm"
                  >
                    {showResolved
                      ? `Hide Resolved (${resolvedCount})`
                      : `Show Resolved (${resolvedCount})`}
                  </Button>
                )}
              </div>
            </div>

            {displayedFeedback.length === 0 ? (
              <div className="text-center text-gray-500 p-6">
                <p className="text-sm">
                  {showResolved
                    ? "No resolved feedback entries."
                    : "No unresolved feedback entries."}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {displayedFeedback.map((item) => (
                  <FeedbackCard
                    key={item._id}
                    feedback={item}
                    canResolve={canResolve}
                    onResolveToggle={handleResolveToggle}
                    isResolving={resolvingId === item._id}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
};

export default PageFeedbackModal;
